import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_API_URL || '';
const RENDER_DPI = 150;
const ACCENT = '#f59e0b';

// ── helpers ────────────────────────────────────────────────────────────────────

function btnStyle(active, color = ACCENT) {
  return {
    padding: '8px 14px',
    borderRadius: '8px',
    border: `1.5px solid ${active ? color : 'rgba(255,255,255,0.12)'}`,
    background: active ? `${color}20` : 'rgba(255,255,255,0.04)',
    color: active ? color : 'rgba(255,255,255,0.75)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '12px',
    fontWeight: 700,
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  };
}

// ── main component ─────────────────────────────────────────────────────────────

export default function PdfTableBoxCard({ lang }) {
  // file
  const [file, setFile]               = useState(null);
  const [fileDrag, setFileDrag]       = useState(false);
  const fileInputRef                  = useRef(null);

  // page viewer
  const [pageNum, setPageNum]         = useState(1);
  const [pageCount, setPageCount]     = useState(0);
  const [pageData, setPageData]       = useState(null);   // { image, width, height }
  const [renderLoading, setRenderLoading] = useState(false);

  // box drawing (CSS-pixel coords relative to the overlay div)
  const [dragStart, setDragStart]     = useState(null);
  const [dragCurrent, setDragCurrent] = useState(null);
  const [box, setBox]                 = useState(null);   // natural-pixel { x0,y0,x1,y1 }
  const overlayRef                    = useRef(null);
  const imgRef                        = useRef(null);

  // multi-fragment
  const [fragments, setFragments]     = useState([]);

  // result
  const [result, setResult]           = useState(null);
  const [converting, setConverting]   = useState(false);

  // mode toggle: 'box' (default) | 'auto'
  const [mode, setMode]               = useState('box');
  const [autoLoading, setAutoLoading] = useState(false);

  // zoom for page viewer (1.0 = fit width, higher = zoomed in for mobile precision)
  const [zoom, setZoom]               = useState(1.0);

  // ── page loader ──────────────────────────────────────────────────────────────

  const loadPage = useCallback(async (f, pg) => {
    if (!f) return;
    setRenderLoading(true);
    setBox(null);
    setDragStart(null);
    setDragCurrent(null);
    setZoom(1.0);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('page_number', pg);
      fd.append('dpi', RENDER_DPI);
      const { data } = await axios.post(`${API}/api/extract/pdf-page-render`, fd, { timeout: 60000 });
      setPageData(data);
      setPageCount(data.page_count);
    } catch {
      toast.error('Failed to render page.');
    } finally {
      setRenderLoading(false);
    }
  }, []);

  useEffect(() => {
    if (file && mode === 'box') loadPage(file, pageNum);
  }, [file, pageNum, mode, loadPage]);

  // ── file handling ─────────────────────────────────────────────────────────────

  const acceptFile = (f) => {
    if (!f || !f.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file.');
      return;
    }
    setFile(f);
    setPageNum(1);
    setBox(null);
    setFragments([]);
    setResult(null);
    setPageData(null);
  };

  const onFileDrop = (e) => {
    e.preventDefault();
    setFileDrag(false);
    acceptFile(e.dataTransfer.files[0]);
  };

  // ── coordinate helpers ────────────────────────────────────────────────────────

  // Convert overlay-relative CSS coords to natural image pixels
  const cssToNatural = (cx, cy) => {
    if (!imgRef.current || !pageData) return { x: cx, y: cy };
    const ir = imgRef.current.getBoundingClientRect();
    const or = overlayRef.current.getBoundingClientRect();
    const imgLeft = ir.left - or.left;
    const imgTop  = ir.top  - or.top;
    const scaleX  = pageData.width  / ir.width;
    const scaleY  = pageData.height / ir.height;
    return {
      x: Math.max(0, Math.min(pageData.width,  Math.round((cx - imgLeft) * scaleX))),
      y: Math.max(0, Math.min(pageData.height, Math.round((cy - imgTop)  * scaleY))),
    };
  };

  // Convert a natural-pixel box back to overlay-relative CSS coords
  const naturalToCSS = (nx, ny) => {
    if (!imgRef.current || !pageData) return { x: nx, y: ny };
    const ir = imgRef.current.getBoundingClientRect();
    const or = overlayRef.current.getBoundingClientRect();
    const imgLeft = ir.left - or.left;
    const imgTop  = ir.top  - or.top;
    const scaleX  = ir.width  / pageData.width;
    const scaleY  = ir.height / pageData.height;
    return { x: nx * scaleX + imgLeft, y: ny * scaleY + imgTop };
  };

  // Unified pointer position from mouse or touch event
  const getRelPos = (e) => {
    if (!overlayRef.current) return { x: 0, y: 0 };
    const r = overlayRef.current.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e.changedTouches ? e.changedTouches[0] : e;
    return { x: src.clientX - r.left, y: src.clientY - r.top };
  };

  // ── box drawing (mouse + touch via pointer events) ────────────────────────────

  const onPointerDown = (e) => {
    e.preventDefault();
    overlayRef.current?.setPointerCapture?.(e.pointerId);
    const pos = getRelPos(e);
    setDragStart(pos);
    setDragCurrent(pos);
    setBox(null);
  };

  const onPointerMove = (e) => {
    if (!dragStart) return;
    setDragCurrent(getRelPos(e));
  };

  const onPointerUp = (e) => {
    if (!dragStart) return;
    const end = getRelPos(e);
    const p0  = cssToNatural(Math.min(dragStart.x, end.x), Math.min(dragStart.y, end.y));
    const p1  = cssToNatural(Math.max(dragStart.x, end.x), Math.max(dragStart.y, end.y));
    if (p1.x - p0.x > 8 && p1.y - p0.y > 8) {
      setBox({ x0: p0.x, y0: p0.y, x1: p1.x, y1: p1.y });
    }
    setDragStart(null);
    setDragCurrent(null);
  };

  // Keep legacy mouse aliases so nothing breaks if any handler is missed
  const onMouseDown  = onPointerDown;
  const onMouseMove  = onPointerMove;
  const onMouseUp    = onPointerUp;

  // Active selection rectangle in overlay-relative CSS coords
  const selRect = (() => {
    if (dragStart && dragCurrent) {
      return {
        left:   Math.min(dragStart.x, dragCurrent.x),
        top:    Math.min(dragStart.y, dragCurrent.y),
        width:  Math.abs(dragCurrent.x - dragStart.x),
        height: Math.abs(dragCurrent.y - dragStart.y),
        color:  ACCENT,
      };
    }
    if (box) {
      const tl = naturalToCSS(box.x0, box.y0);
      const br = naturalToCSS(box.x1, box.y1);
      return {
        left:   tl.x,
        top:    tl.y,
        width:  br.x - tl.x,
        height: br.y - tl.y,
        color:  '#00d4aa',
      };
    }
    return null;
  })();

  // ── actions ───────────────────────────────────────────────────────────────────

  const handleConvert = async () => {
    if (!file || !box) return;
    const thisFrag = { page: pageNum, box: [box.x0, box.y0, box.x1, box.y1] };
    const allFrags = fragments.length > 0 ? [...fragments, thisFrag] : [thisFrag];
    setConverting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('dpi', RENDER_DPI);
      fd.append('fragments', JSON.stringify(allFrags));
      const { data } = await axios.post(`${API}/api/extract/pdf-table-box`, fd, { timeout: 300000 });
      setResult(data.tex);
      setFragments([]);
      toast.success('Table extracted!', {
        style: { background: '#13131f', color: '#00d4aa', border: '1px solid rgba(0,212,170,0.3)' },
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Extraction failed.');
    } finally {
      setConverting(false);
    }
  };

  const handleAddContinuation = () => {
    if (!box) return;
    setFragments(prev => [...prev, { page: pageNum, box: [box.x0, box.y0, box.x1, box.y1] }]);
    setBox(null);
    toast.success(`Fragment ${fragments.length + 1} saved — navigate to the next page.`);
  };

  const handleAutoExtract = async () => {
    if (!file) return;
    setAutoLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${API}/api/extract/pdf-tables-latex`, fd, {
        responseType: 'blob', timeout: 1200000,
      });
      const text = await resp.data.text();
      setResult(text);
      toast.success('Auto-extract complete!', {
        style: { background: '#13131f', color: '#00d4aa', border: '1px solid rgba(0,212,170,0.3)' },
      });
    } catch (err) {
      const detail = err.response?.data instanceof Blob
        ? await err.response.data.text().then(t => { try { return JSON.parse(t).detail; } catch { return t; } })
        : err.response?.data?.detail;
      toast.error(detail || 'Auto-extract failed.');
    } finally {
      setAutoLoading(false);
    }
  };

  const downloadResult = () => {
    const blob = new Blob([result], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = (file?.name?.replace(/\.pdf$/i, '') || 'table') + '_extracted.tex';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── render ────────────────────────────────────────────────────────────────────

  const noFile = !file;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

      {/* Mode toggle */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button style={btnStyle(mode === 'auto')} onClick={() => setMode('auto')}>
          ⊞ {lang === 'ar' ? 'استخراج جداول ذات حدود كاملة' : lang === 'fa' ? 'استخراج جداول با کادر کامل' : 'Extract Fully Bordered'}
        </button>
        <button style={btnStyle(mode === 'box')}  onClick={() => setMode('box')}>
          ✏️ {lang === 'ar' ? 'استخراج جداول بدون حدود' : lang === 'fa' ? 'استخراج جداول بدون کادر' : 'Extract Borderless / Booktabs'}
        </button>
      </div>

      {/* File drop zone (shared by both modes) */}
      {noFile ? (
        <div
          onDragOver={e => { e.preventDefault(); setFileDrag(true); }}
          onDragLeave={() => setFileDrag(false)}
          onDrop={onFileDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${fileDrag ? ACCENT : 'rgba(255,255,255,0.18)'}`,
            borderRadius: '12px',
            padding: '32px 16px',
            textAlign: 'center',
            cursor: 'pointer',
            background: fileDrag ? `${ACCENT}0a` : 'transparent',
            transition: 'all 0.2s',
          }}
        >
          <div style={{ fontSize: '28px', marginBottom: '8px' }}>📄</div>
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '13px' }}>
            Drop a PDF here or <span style={{ color: ACCENT }}>click to browse</span>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            style={{ display: 'none' }}
            onChange={e => acceptFile(e.target.files[0])}
          />
        </div>
      ) : (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'rgba(255,255,255,0.05)', borderRadius: '9px', padding: '8px 12px',
        }}>
          <span style={{ fontSize: '12px', color: ACCENT, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '220px' }}>
            📄 {file.name}
          </span>
          <button
            onClick={() => { setFile(null); setPageData(null); setFragments([]); setResult(null); setBox(null); }}
            style={{ ...btnStyle(false), padding: '4px 10px', fontSize: '11px' }}
          >
            ✕ Change
          </button>
        </div>
      )}

      {/* ── BOX MODE ──────────────────────────────────────────────────────────── */}
      {mode === 'box' && file && (
        <>
          {/* Page viewer */}
          <div style={{
            background: 'rgba(0,0,0,0.35)', borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden',
          }}>
            {/* Nav bar */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '7px 12px', gap: '6px', flexWrap: 'wrap',
              borderBottom: '1px solid rgba(255,255,255,0.07)',
            }}>
              <button
                style={{ ...btnStyle(false), minHeight: '36px', minWidth: '60px' }}
                disabled={pageNum <= 1 || renderLoading}
                onClick={() => setPageNum(p => p - 1)}
              >← Prev</button>
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap' }}>
                {renderLoading ? 'Loading…' : `Page ${pageNum} / ${pageCount || '?'}`}
              </span>
              {/* Zoom controls for precise touch drawing */}
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <button
                  style={{ ...btnStyle(false), padding: '6px 10px', minHeight: '36px', minWidth: '36px', fontSize: '16px' }}
                  onClick={() => setZoom(z => Math.max(1.0, +(z - 0.5).toFixed(1)))}
                  disabled={zoom <= 1.0}
                  title="Zoom out"
                >−</button>
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', minWidth: '32px', textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
                <button
                  style={{ ...btnStyle(false), padding: '6px 10px', minHeight: '36px', minWidth: '36px', fontSize: '16px' }}
                  onClick={() => setZoom(z => Math.min(4.0, +(z + 0.5).toFixed(1)))}
                  disabled={zoom >= 4.0}
                  title="Zoom in"
                >+</button>
              </div>
              <button
                style={{ ...btnStyle(false), minHeight: '36px', minWidth: '60px' }}
                disabled={pageNum >= pageCount || renderLoading}
                onClick={() => setPageNum(p => p + 1)}
              >Next →</button>
            </div>

            {/* Canvas area — scrollable zoom container */}
            <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
              <div style={{ position: 'relative', lineHeight: 0, minHeight: '120px', width: `${Math.round(zoom * 100)}%`, minWidth: '100%' }}>
                {renderLoading && (
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', background: 'rgba(0,0,0,0.55)', zIndex: 2,
                  }}>
                    <span className="spinner" />
                  </div>
                )}

                {pageData && (
                  <>
                    <img
                      ref={imgRef}
                      src={`data:image/png;base64,${pageData.image}`}
                      alt="PDF page"
                      style={{ display: 'block', width: '100%', userSelect: 'none', pointerEvents: 'none' }}
                      draggable={false}
                    />
                    {/* Unified pointer overlay (mouse + touch) */}
                    <div
                      ref={overlayRef}
                      onPointerDown={onPointerDown}
                      onPointerMove={onPointerMove}
                      onPointerUp={onPointerUp}
                      onPointerLeave={onPointerUp}
                      onMouseDown={onMouseDown}
                      onMouseMove={onMouseMove}
                      onMouseUp={onMouseUp}
                      onMouseLeave={onMouseUp}
                      style={{
                        position: 'absolute', inset: 0, cursor: 'crosshair', userSelect: 'none',
                        touchAction: 'none',
                      }}
                    >
                      {selRect && (
                        <div style={{
                          position: 'absolute',
                          left:   selRect.left,
                          top:    selRect.top,
                          width:  selRect.width,
                          height: selRect.height,
                          border: `2px solid ${selRect.color}`,
                          background: `${selRect.color}18`,
                          pointerEvents: 'none',
                          boxSizing: 'border-box',
                        }} />
                      )}
                    </div>
                  </>
                )}

                {!pageData && !renderLoading && (
                  <div style={{ padding: '32px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '12px' }}>
                    Rendering page…
                  </div>
                )}
              </div>
            </div>

            {/* Action footer — always inside the viewer so it stays in view */}
            {pageData && (
              <div style={{
                borderTop: '1px solid rgba(255,255,255,0.07)',
                padding: '8px 12px',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
              }}>
                {box ? (
                  <div style={{ display: 'flex', gap: '8px', width: '100%', flexWrap: 'wrap' }}>
                    <button
                      className="btn-primary"
                      style={{ flex: 1, minWidth: '140px', padding: '10px 14px', fontSize: '13px', minHeight: '44px' }}
                      onClick={handleConvert}
                      disabled={converting}
                    >
                      {converting
                        ? <><span className="spinner" /> Converting…</>
                        : fragments.length > 0
                          ? `⚡ Convert stitched (${fragments.length + 1} frags)`
                          : '⚡ Convert this table'
                      }
                    </button>
                    <button
                      style={{ ...btnStyle(false, '#00d4aa'), flex: '0 0 auto', minHeight: '44px', padding: '10px 14px' }}
                      onClick={handleAddContinuation}
                      title="Save this box and navigate to the next page to add another fragment"
                    >
                      + Add continuation
                    </button>
                    <button
                      style={{ ...btnStyle(false), flex: '0 0 auto', padding: '10px 12px', minHeight: '44px', minWidth: '44px' }}
                      onClick={() => setBox(null)}
                      title="Clear selection"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', width: '100%', textAlign: 'center' }}>
                    {dragStart
                      ? 'Release to commit selection'
                      : fragments.length > 0
                        ? 'Draw a box on this page, then click "Convert stitched" or "Add continuation"'
                        : 'Draw a rectangle around the table you want to extract'
                    }
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Fragment list */}
          {fragments.length > 0 && (
            <div style={{ background: 'rgba(0,212,170,0.06)', border: '1px solid rgba(0,212,170,0.2)', borderRadius: '9px', padding: '10px 12px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#00d4aa', marginBottom: '6px' }}>
                Queued fragments ({fragments.length})
              </div>
              {fragments.map((f, i) => (
                <div key={i} style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>#{i + 1} — page {f.page}, box [{f.box.map(v => Math.round(v)).join(', ')}]</span>
                  <button
                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '11px', padding: 0 }}
                    onClick={() => setFragments(prev => prev.filter((_, j) => j !== i))}
                  >✕</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── AUTO MODE ─────────────────────────────────────────────────────────── */}
      {mode === 'auto' && file && (
        <button
          className="btn-primary"
          onClick={handleAutoExtract}
          disabled={autoLoading}
        >
          {autoLoading
            ? <><span className="spinner" />{lang === 'ar' ? 'جاري الاستخراج...' : lang === 'fa' ? 'در حال استخراج...' : 'Extracting…'}</>
            : lang === 'ar' ? '⚡ استخراج جميع الجداول ذات الحدود' : lang === 'fa' ? '⚡ استخراج همه جداول با کادر' : '⚡ Extract all bordered tables'
          }
        </button>
      )}

      {/* ── RESULT ────────────────────────────────────────────────────────────── */}
      {result && (
        <div style={{
          background: 'rgba(0,0,0,0.35)',
          border: '1px solid rgba(0,212,170,0.25)',
          borderRadius: '10px',
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '9px 12px',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
          }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#00d4aa' }}>
              ✓ LaTeX output
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                style={btnStyle(false, '#00d4aa')}
                onClick={() => { navigator.clipboard.writeText(result); toast.success('Copied!'); }}
              >
                Copy
              </button>
              <button style={btnStyle(true, '#00d4aa')} onClick={downloadResult}>
                ↓ .tex
              </button>
              <button
                style={{ ...btnStyle(false), padding: '4px 8px' }}
                onClick={() => setResult(null)}
              >✕</button>
            </div>
          </div>
          <pre style={{
            margin: 0, padding: '12px',
            fontSize: '11px', lineHeight: 1.6,
            color: 'rgba(255,255,255,0.72)',
            fontFamily: 'monospace',
            overflowX: 'auto',
            maxHeight: '260px',
            overflowY: 'auto',
          }}>
            {result.length > 4000 ? result.slice(0, 4000) + '\n…(truncated)' : result}
          </pre>
        </div>
      )}
    </div>
  );
}
